// Fleet — equipment detail (Equipment Detail CP). Sibling of VehicleDetail:
// same maintenance-schedules + service-history sections (shared modals), minus
// the odometer concept (equipment has no odometer). Schedules are date-only —
// the shared ScheduleModal hides the mileage option for equipment targets.
import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { ArrowLeft, Plus, Edit3 } from 'lucide-react'
import {
  ExpiryBadge, StatusPill, EQUIP_STATUS, EquipmentModal, type EquipmentRow,
} from './FleetPage'
import { scheduleDueState, formatInterval, type MaintenanceSchedule } from './maintenanceUtils'
import { DueBadge, ScheduleModal, RecordModal } from './MaintenanceModals'

interface RecordRow {
  id: string; performed_at: string; description: string; cost: number | null
  performed_by: string | null; schedule_id: string | null
}

const sectionTitle: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }
const card: React.CSSProperties = { background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: 18 }

export default function EquipmentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const qc = useQueryClient()
  const [editOpen, setEditOpen] = useState(false)
  const [schedOpen, setSchedOpen] = useState(false)
  const [schedEditing, setSchedEditing] = useState<MaintenanceSchedule | null>(null)
  const [recOpen, setRecOpen] = useState(false)

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['equipment-item', id] })
    qc.invalidateQueries({ queryKey: ['equipment-schedules', id] })
    qc.invalidateQueries({ queryKey: ['equipment-records', id] })
    qc.invalidateQueries({ queryKey: ['equipment', workspace?.id] })
    qc.invalidateQueries({ queryKey: ['fleet-equipment-schedules', workspace?.id] })
  }

  const { data: item, isLoading } = useQuery<EquipmentRow | null>({
    queryKey: ['equipment-item', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('equipment').select('*').eq('id', id!).single()
      if (error) { console.error('[fleet] equipment query failed', error); throw error }
      return data as EquipmentRow
    },
    enabled: !!id,
  })
  const { data: schedules = [] } = useQuery<MaintenanceSchedule[]>({
    queryKey: ['equipment-schedules', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_schedules').select('*').eq('equipment_id', id!).order('created_at', { ascending: false })
      if (error) { console.error('[fleet] equipment schedules query failed', error); throw error }
      return (data ?? []) as MaintenanceSchedule[]
    },
    enabled: !!id,
  })
  const { data: records = [] } = useQuery<RecordRow[]>({
    queryKey: ['equipment-records', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('maintenance_records').select('id, performed_at, description, cost, performed_by, schedule_id').eq('equipment_id', id!).order('performed_at', { ascending: false })
      if (error) { console.error('[fleet] equipment records query failed', error); throw error }
      return (data ?? []) as RecordRow[]
    },
    enabled: !!id,
  })
  const { data: techs = [] } = useQuery<Array<{ id: string; full_name: string | null }>>({
    queryKey: ['fleet-techs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name').eq('workspace_id', workspace.id)
      return data ?? []
    },
    enabled: !!workspace?.id,
  })
  const { data: vehicles = [] } = useQuery<Array<{ id: string; name: string; status: string }>>({
    queryKey: ['fleet-vehicles-picker', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('vehicles').select('id, name, status').eq('workspace_id', workspace.id).order('status').order('name')
      return data ?? []
    },
    enabled: !!workspace?.id,
  })

  if (isLoading) return <div style={{ padding: 40, color: 'var(--ink-tertiary)' }}>Loading…</div>
  if (!item) return <div style={{ padding: 40, color: 'var(--ink-tertiary)' }}>Equipment not found. <Link to="/app/fleet?tab=equipment" style={{ color: 'var(--accent-text)' }}>Back to Fleet</Link></div>

  const s = EQUIP_STATUS[item.status] ?? EQUIP_STATUS.active
  const nowMs = Date.now()
  const assignment = item.assigned_agent_id
    ? (techs.find(t => t.id === item.assigned_agent_id)?.full_name ?? 'Unknown')
    : item.assigned_vehicle_id
      ? `Vehicle · ${vehicles.find(v => v.id === item.assigned_vehicle_id)?.name ?? 'Unknown'}`
      : 'Unassigned'
  const subtitle = [item.category, item.serial_number ? `SN ${item.serial_number}` : null, item.purchase_date ? `bought ${item.purchase_date}` : null].filter(Boolean).join(' · ')

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <button onClick={() => navigate('/app/fleet?tab=equipment')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--ink-tertiary)', fontSize: 13, cursor: 'pointer', padding: 0, width: 'fit-content' }}>
        <ArrowLeft style={{ width: 15, height: 15 }} /> Fleet
      </button>

      {/* Header */}
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', margin: 0, color: 'var(--ink-primary)' }}>{item.name}</h1>
            <StatusPill meta={s} />
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '6px 0 0' }}>{subtitle || '—'}</p>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <ExpiryBadge date={item.warranty_expires_at} label="Warranty" />
            <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Assigned: <span style={{ color: 'var(--ink-secondary)' }}>{assignment}</span></span>
          </div>
        </div>
        <button onClick={() => setEditOpen(true)} className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 'fit-content' }}>
          <Edit3 style={{ width: 14, height: 14 }} /> Edit
        </button>
      </div>

      {/* Maintenance schedules (date-only for equipment) */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={sectionTitle}>Maintenance schedules</h2>
          <button onClick={() => { setSchedEditing(null); setSchedOpen(true) }} className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}><Plus style={{ width: 13, height: 13 }} /> Add</button>
        </div>
        {schedules.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No schedules yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {schedules.map(sc => {
              const st = scheduleDueState(sc, null, nowMs)
              const due = sc.next_due_date ? `Next: ${sc.next_due_date}` : 'No next due'
              return (
                <div key={sc.id} onClick={() => { setSchedEditing(sc); setSchedOpen(true) }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border-subtle)', cursor: 'pointer', opacity: sc.active ? 1 : 0.55 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{sc.title}{!sc.active && <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 400 }}> · paused</span>}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{formatInterval(sc)} · {due}</div>
                  </div>
                  <DueBadge state={st} />
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Service history */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={sectionTitle}>Service history</h2>
          <button onClick={() => setRecOpen(true)} className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}><Plus style={{ width: 13, height: 13 }} /> Log service</button>
        </div>
        {records.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No service logged yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {records.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '8px 10px', borderRadius: 10, border: '1px solid var(--border-subtle)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{r.description}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{r.performed_at}{r.performed_by ? ` · ${r.performed_by}` : ''}</div>
                </div>
                {r.cost != null && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-secondary)', whiteSpace: 'nowrap' }}>${r.cost.toFixed(2)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      <EquipmentModal open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); invalidate() }} workspaceId={workspace?.id ?? null} editing={item} techs={techs} vehicles={vehicles} />
      <ScheduleModal open={schedOpen} onClose={() => setSchedOpen(false)} onSaved={() => { setSchedOpen(false); invalidate() }} workspaceId={workspace?.id ?? null} target={{ equipmentId: id! }} editing={schedEditing} />
      <RecordModal open={recOpen} onClose={() => setRecOpen(false)} onSaved={() => { setRecOpen(false); invalidate() }} workspaceId={workspace?.id ?? null} target={{ equipmentId: id! }} schedules={schedules} />
    </div>
  )
}
